<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import type { Purchase } from '../../modules/paymentsApi/purchases/purchase.entity'
    import Zinput from '../_common/z/zinput.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import dayjs from 'dayjs'
    import { ScheduledCancelNextInstallment } from '../../modules/paymentsApi/purchases/purchase.enum'
    import { ScheduleCancelInputDto, ScheduleCancelOutputDto } from '../../modules/paymentsApi/purchases/dto/schedule-cancel.dto'

    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    export let purchaseId: string

    // a api aceita encerramento entre 7 e 30 dias a partir de hoje
    const MIN_DAYS = 7
    const MAX_DAYS = 30

    let entity: ScheduleCancelInputDto = new ScheduleCancelInputDto()
    entity.nextInstallment = ScheduledCancelNextInstallment.prorated
    let purchase: Purchase = null

    let step = 1
    let saving = false
    let simulation: ScheduleCancelOutputDto = null

    const minDate = dayjs().add(MIN_DAYS, 'day').format('YYYY-MM-DD')
    const maxDate = dayjs().add(MAX_DAYS, 'day').format('YYYY-MM-DD')

    const options = [
        { value: ScheduledCancelNextInstallment.prorated, label: 'Cobrar proporcional aos dias usados' },
        { value: ScheduledCancelNextInstallment.full, label: 'Cobrar o valor cheio' },
        { value: ScheduledCancelNextInstallment.cancel, label: 'Não cobrar' },
    ]

    async function loadPurchase() {
        purchase = await window.api.payments.purchases.getOne(purchaseId)
    }

    onMount(async () => {
        await loadPurchase()
    })

    async function goSimulate() {
        try {
            if (!entity.scheduledCancelAt) {
                window.error('Informe a data do cancelamento')
                return
            }
            entity.simulation = true
            simulation = await window.api.payments.purchases.scheduleCancel(purchaseId, entity)
            step = 2
        } catch (error) {
            window.error(error.message || error)
        }
    }

    async function goSave() {
        try {
            saving = true
            entity.simulation = false
            await window.api.payments.purchases.scheduleCancel(purchaseId, entity)
            dispatch('close')
        } catch (error) {
            window.error(error.message || error)
        } finally {
            saving = false
        }
    }

    // texto do que acontece com a última cobrança, já considerando o que a api decidiu aplicar
    function chargeSummary(sim: ScheduleCancelOutputDto): string {
        if (!sim.charge) return 'Não há cobrança em aberto a tratar.'
        const dueDate = dayjs(sim.charge.dueDate).format('DD/MM/YYYY')
        if (sim.charge.action === 'aborted') {
            return `A cobrança de ${dueDate} não será feita.`
        }
        if (sim.charge.action === 'adjusted') {
            return `A cobrança de ${dueDate} passa de ${window.api.utils.utils.toReal(sim.charge.currentValue)} para ${window.api.utils.utils.toReal(
                sim.charge.newValue,
            )}, referente a ${sim.daysUsed} dia(s) de uso.`
        }
        if (sim.charge.action === 'kept') {
            return `A cobrança de ${dueDate} será feita normalmente, no valor de ${window.api.utils.utils.toReal(sim.charge.currentValue)}.`
        }
        return 'A cobrança já saiu para o banco e não pôde ser alterada.'
    }
</script>

<div class="root">
    <Zact>
        <div class="spacer" />
        {#if window.store.token.permissions.pay.entries.write}
            {#if step === 1}
                <Zbutton label="Simular" on:click={goSimulate}></Zbutton>
            {:else}
                <Zbutton label="Voltar" color="yellow" on:click={() => (step = 1)}></Zbutton>
                <Zbutton label="Confirmar encerramento" color="green" loading={saving} disabled={saving} on:click={goSave}></Zbutton>
            {/if}
        {/if}
    </Zact>

    <div class="alert alert-primary text-center m-3">
        A data do cancelamento deve ficar entre {dayjs(minDate).format('DD/MM/YYYY')} e {dayjs(maxDate).format('DD/MM/YYYY')}. Até lá a assinatura continua
        ativa.
    </div>

    <Zframe title="Cancelar assinatura" class="m-3">
        {#if step === 1}
            <ZContainer gapRow="8px" gapCol="8px">
                <Zinput size={6} sizeM={12} label="Encerrar em" type="date" bind:value={entity.scheduledCancelAt}></Zinput>
                <Zinput size={6} sizeM={12} label="Próxima cobrança" type="select" {options} bind:value={entity.nextInstallment}></Zinput>
            </ZContainer>
        {:else}
            <div>A assinatura será encerrada em {dayjs(simulation.scheduledCancelAt).format('DD/MM/YYYY')}.</div>
            <div>{chargeSummary(simulation)}</div>
            {#if simulation.reason}
                <div class="reason">{simulation.reason}</div>
            {/if}
            <div class="mt-2">Depois do encerramento nenhuma nova cobrança é gerada.</div>
        {/if}
    </Zframe>
</div>

<style>
    .reason {
        margin-top: 6px;
        opacity: 0.85;
        font-style: italic;
    }
</style>
